import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { assetService } from '../services/assetService';
import { useAuth } from './AuthContext';

// Initial state
const initialState = {
  assets: [],
  assetStats: null,
  loading: false,
  error: null,
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  },
  filters: {
    search: '',
    type: '',
    status: '',
    department: '',
    assignedTo: '',
    minValue: '',
    maxValue: ''
  },
  sort: {
    field: 'createdAt',
    order: 'desc'
  }
};

// Action types
const ASSET_ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  SET_ASSETS: 'SET_ASSETS',
  SET_ASSET_STATS: 'SET_ASSET_STATS',
  ADD_ASSET: 'ADD_ASSET',
  UPDATE_ASSET: 'UPDATE_ASSET',
  DELETE_ASSET: 'DELETE_ASSET',
  SET_FILTERS: 'SET_FILTERS',
  SET_SORT: 'SET_SORT',
  SET_PAGINATION: 'SET_PAGINATION',
  CLEAR_ERROR: 'CLEAR_ERROR'
};

// Reducer
const assetReducer = (state, action) => {
  switch (action.type) {
    case ASSET_ACTIONS.SET_LOADING:
      return { ...state, loading: action.payload };
    
    case ASSET_ACTIONS.SET_ERROR:
      return { ...state, error: action.payload, loading: false };
    
    case ASSET_ACTIONS.SET_ASSETS:
      return {
        ...state,
        assets: action.payload.assets,
        pagination: action.payload.pagination,
        loading: false,
        error: null
      };
    
    case ASSET_ACTIONS.SET_ASSET_STATS:
      return { ...state, assetStats: action.payload, loading: false };
    
    case ASSET_ACTIONS.ADD_ASSET:
      return {
        ...state,
        assets: [action.payload, ...state.assets],
        pagination: {
          ...state.pagination,
          totalItems: state.pagination.totalItems + 1
        }
      };
    
    case ASSET_ACTIONS.UPDATE_ASSET:
      return {
        ...state,
        assets: state.assets.map(asset =>
          asset._id === action.payload._id ? action.payload : asset
        )
      };
    
    case ASSET_ACTIONS.DELETE_ASSET:
      return {
        ...state,
        assets: state.assets.filter(asset => asset._id !== action.payload),
        pagination: {
          ...state.pagination,
          totalItems: Math.max(0, state.pagination.totalItems - 1)
        }
      };
    
    case ASSET_ACTIONS.SET_FILTERS:
      return {
        ...state,
        filters: { ...state.filters, ...action.payload },
        pagination: { ...state.pagination, currentPage: 1 }
      };
    
    case ASSET_ACTIONS.SET_SORT:
      return { ...state, sort: action.payload };
    
    case ASSET_ACTIONS.SET_PAGINATION:
      return { ...state, pagination: { ...state.pagination, ...action.payload } };
    
    case ASSET_ACTIONS.CLEAR_ERROR:
      return { ...state, error: null };
    
    default:
      return state;
  }
};

// Context
const AssetContext = createContext();

// Provider component
export const AssetProvider = ({ children }) => {
  const [state, dispatch] = useReducer(assetReducer, initialState);
  const { isAuthenticated, loading: authLoading } = useAuth();

  // Load assets
  const loadAssets = async (params = {}) => {
    try {
      dispatch({ type: ASSET_ACTIONS.SET_LOADING, payload: true });
      
      const queryParams = {
        page: state.pagination.currentPage,
        limit: state.pagination.itemsPerPage,
        sort: state.sort.field,
        order: state.sort.order,
        ...state.filters,
        ...params
      };

      // Remove empty filter values
      Object.keys(queryParams).forEach(key => {
        if (queryParams[key] === '' || queryParams[key] === null || queryParams[key] === undefined) {
          delete queryParams[key];
        }
      });

      const response = await assetService.getAssets(queryParams);
      
      dispatch({
        type: ASSET_ACTIONS.SET_ASSETS,
        payload: {
          assets: response.data.assets,
          pagination: response.data.pagination
        }
      });
    } catch (error) {
      dispatch({ type: ASSET_ACTIONS.SET_ERROR, payload: error.message || 'Failed to load assets' });
    }
  };

  // Load asset statistics
  const loadAssetStats = async () => {
    try {
      dispatch({ type: ASSET_ACTIONS.SET_LOADING, payload: true });
      const response = await assetService.getAssetStats();
      dispatch({ type: ASSET_ACTIONS.SET_ASSET_STATS, payload: response.data });
    } catch (error) {
      dispatch({ type: ASSET_ACTIONS.SET_ERROR, payload: error.message || 'Failed to load asset statistics' });
    }
  };

  // Create asset
  const createAsset = async (assetData) => {
    try {
      dispatch({ type: ASSET_ACTIONS.SET_LOADING, payload: true });
      const response = await assetService.createAsset(assetData);
      dispatch({ type: ASSET_ACTIONS.ADD_ASSET, payload: response.data.asset });
      dispatch({ type: ASSET_ACTIONS.SET_LOADING, payload: false });
      return response.data.asset;
    } catch (error) {
      dispatch({ type: ASSET_ACTIONS.SET_ERROR, payload: error.message || 'Failed to create asset' });
      dispatch({ type: ASSET_ACTIONS.SET_LOADING, payload: false });
      throw error;
    }
  };

  // Update asset
  const updateAsset = async (id, assetData) => {
    try {
      dispatch({ type: ASSET_ACTIONS.SET_LOADING, payload: true });
      const response = await assetService.updateAsset(id, assetData);
      dispatch({ type: ASSET_ACTIONS.UPDATE_ASSET, payload: response.data.asset });
      dispatch({ type: ASSET_ACTIONS.SET_LOADING, payload: false });
      return response.data.asset;
    } catch (error) {
      dispatch({ type: ASSET_ACTIONS.SET_ERROR, payload: error.message || 'Failed to update asset' });
      dispatch({ type: ASSET_ACTIONS.SET_LOADING, payload: false });
      throw error;
    }
  };

  // Delete asset
  const deleteAsset = async (id) => {
    try {
      dispatch({ type: ASSET_ACTIONS.SET_LOADING, payload: true });
      await assetService.deleteAsset(id);
      dispatch({ type: ASSET_ACTIONS.DELETE_ASSET, payload: id });
      dispatch({ type: ASSET_ACTIONS.SET_LOADING, payload: false });
    } catch (error) {
      dispatch({ type: ASSET_ACTIONS.SET_ERROR, payload: error.message || 'Failed to delete asset' });
      dispatch({ type: ASSET_ACTIONS.SET_LOADING, payload: false });
      throw error;
    }
  };

  // Set filters
  const setFilters = (filters) => {
    dispatch({ type: ASSET_ACTIONS.SET_FILTERS, payload: filters });
  };

  // Set sort
  const setSort = (sort) => {
    dispatch({ type: ASSET_ACTIONS.SET_SORT, payload: sort });
  };

  // Set pagination
  const setPagination = (pagination) => {
    dispatch({ type: ASSET_ACTIONS.SET_PAGINATION, payload: pagination });
  };

  // Clear error
  const clearError = () => {
    dispatch({ type: ASSET_ACTIONS.CLEAR_ERROR });
  };

  // Load assets when filters, sort, or pagination changes (only if authenticated and auth is loaded)
  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      loadAssets();
    }
  }, [isAuthenticated, authLoading, state.filters, state.sort, state.pagination.currentPage, state.pagination.itemsPerPage]);

  // Load asset stats on mount (only if authenticated and auth is loaded)
  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      loadAssetStats();
    }
  }, [isAuthenticated, authLoading]);

  const value = {
    ...state,
    loadAssets,
    loadAssetStats,
    createAsset,
    updateAsset,
    deleteAsset,
    setFilters,
    setSort,
    setPagination,
    clearError
  };

  return (
    <AssetContext.Provider value={value}>
      {children}
    </AssetContext.Provider>
  );
};

// Custom hook to use asset context
export const useAssets = () => {
  const context = useContext(AssetContext);
  if (!context) {
    throw new Error('useAssets must be used within an AssetProvider');
  }
  return context;
};

export default AssetContext;
